<script setup lang="ts">
import type { Breadcrumb } from '~/../../types/Breadcrumb'
import type { Campsite } from '../../../../types/Campsite'

interface Props {
  site: Campsite
  breadCrumbs: Breadcrumb[]
  link?: string
  arrivalDate?: string
  duration?: number
}

const props = defineProps<Props>()
const emits = defineEmits(['show-map', 'view-event', 'show-reviews'])
</script>

<template>
  <div class="gap-xxs flex flex-col">
    <div class="flex justify-between">
      <Breadcrumb
        class="text-brand-secondary-400 sm:hidden pb-xxs"
        :bread-crumbs="breadCrumbs"
      />
      <SiteAddToFavorites class="mb-auto sm:hidden" :site="site" />
    </div>
    <div class="flex flex-row gap-xs items-start justify-between">
      <h1 class="font-h1 text-brand-secondary-900">
        {{ props.site.name }}
      </h1>

      <SiteAddToFavorites class="py-2 hidden sm:flex" :site="site" />
    </div>

    <div class="flex flex-col sm:flex-row gap-2 flex-wrap">
      <Breadcrumb
        class="text-brand-secondary-400 hidden sm:block my-auto"
        :bread-crumbs="breadCrumbs"
      />
      <div v-show="props.site.rating != 0" class="h-5 my-auto hidden sm:flex">
        <Divider class="mx-1" vertical />
      </div>
      <div class="flex flex-row gap-x-2">
        <RatingStars
          v-if="props.site.rating != 0"
          class="text-sm self-center"
          :rating="props.site.rating"
        />
        <div v-show="props.site.rating != 0" class="h-5 my-auto">
          <Divider class="mx-1" vertical />
        </div>
        <div
          class="flex gap-2 cursor-pointer self-center"
          @click="emits('show-map')"
        >
          <NuxtImg
            height="22"
            width="22"
            class="-translate-y-1"
            src="icons/misc/map.svg"
          /><span
            class="text-brand-secondary-400 hover:text-brand-secondary-500 text-sm self-center"
            >{{ $t('site-card.map-button') }}</span
          >
        </div>
        <template v-if="site?.ecoLabel">
          <Divider vertical class="h-5" />
          <EcoLabel show-label link-prefix="info" />
        </template>
        <div class="h-5 my-auto">
          <Divider class="mx-1" vertical />
        </div>
        <div
          v-if="site.reviews?.averageScore"
          class="flex gap-2 cursor-pointer"
          @click="emits('show-reviews')"
        >
          <Rating
            class="cursor-pointer -translate-y-[2px]"
            :rating="site.reviews?.averageScore"
          />
          <span
            class="hidden sm:block text-brand-secondary-400 hover:text-brand-secondary-500 text-sm self-center"
            >{{ site.reviews.reviewCount }} {{ $t('review.reviews') }}</span
          >
        </div>
        <span
          v-else
          class="sm:block text-brand-secondary-400 text-sm cursor-auto"
          >{{ $t('review.no-reviews') }}</span
        >
      </div>
    </div>
  </div>
</template>
